import * as THREE from 'three';
import type { BuildingVisual } from './buildingModels';

export interface ModelRequest {
  key: string;
  kind: number;
  civ: number;
  tier: number;
  variant: number;
}

export interface ModelResult {
  key: string;
  attrs: { name: string; array: Float32Array; itemSize: number }[];
  smoke: [number, number, number][];
  height: number;
  scale: number;
}

/**
 * Builds building models on background threads (web workers), so a civilisation advancing an age,
 * or a building type appearing for the first time, does not freeze the game while its models are
 * made (40 to 90 ms each, up to a second for a whole age). Ask for a model ahead of time with
 * `request`; collect it with `take` once it has arrived. Where workers are not available (tests,
 * old browsers) nothing is built in the background and callers build on the spot as before.
 */
export class ModelService {
  private workers: Worker[] = [];
  private next = 0;
  private pending = new Set<string>();
  private ready = new Map<string, BuildingVisual>();

  constructor(count = Math.max(1, Math.min(2, (globalThis.navigator?.hardwareConcurrency ?? 2) - 1))) {
    if (typeof Worker === 'undefined') return;
    try {
      for (let i = 0; i < count; i++) {
        const wk = new Worker(new URL('./modelWorker.ts', import.meta.url), { type: 'module' });
        wk.onmessage = (e: MessageEvent<ModelResult>) => this.receive(e.data);
        wk.onerror = () => this.dispose();
        this.workers.push(wk);
      }
    } catch {
      this.dispose();
    }
  }

  get available(): boolean {
    return this.workers.length > 0;
  }

  /** Queue a model to be built in the background (no-op if already asked for or not available). */
  request(r: ModelRequest): void {
    if (!this.available || this.pending.has(r.key) || this.ready.has(r.key)) return;
    this.pending.add(r.key);
    this.workers[this.next++ % this.workers.length].postMessage(r);
  }

  /** No longer wanted (it was built some other way): drop it when it arrives. */
  forget(key: string): void {
    this.pending.delete(key);
    this.ready.get(key)?.geometry.dispose();
    this.ready.delete(key);
  }

  /** A finished model, handed over once (the caller keeps it). */
  take(key: string): BuildingVisual | null {
    const v = this.ready.get(key);
    if (!v) return null;
    this.ready.delete(key);
    return v;
  }

  private receive(r: ModelResult): void {
    if (!this.pending.delete(r.key)) return;
    const g = new THREE.BufferGeometry();
    for (const a of r.attrs) g.setAttribute(a.name, new THREE.Float32BufferAttribute(a.array, a.itemSize));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    this.ready.set(r.key, { geometry: g, smoke: r.smoke.map(([x, y, z]) => new THREE.Vector3(x, y, z)), height: r.height, scale: r.scale });
  }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers = [];
    this.pending.clear();
    for (const v of this.ready.values()) v.geometry.dispose();
    this.ready.clear();
  }
}
